import React from 'react';
import { Typography, Row, Col, Button } from 'antd';
import { tokens } from '../theme/tokens';
import { CreativityType } from '../types/banner';
import { MenuSections } from '../types/menu-sections';

import { useCreativityForm } from '../hooks/banners/creation/form/use-creativity-form';
import { useTemplateInfo } from '../hooks/banners/use-template-utils';

import { DemoPreview } from '../components/demo-preview';
import { useResponsiveSizes } from '../hooks/use-responsive-sizes';
import { PageTitle } from '../components/ui/layout/page-title';
import { PageContainer } from '../components/ui/layout/page-container';
import { useSubmitFormCreativity } from '../hooks/banners/creation/form/use-submit-creativity-banner';
import { CreativityForm } from '../components/form/creativity-form';

const { Title, Text } = Typography;

interface CreativityFormPageProps {
  creativityType: CreativityType;
  onNavigate: (section: MenuSections) => void;
}

export const CreativityFormPage: React.FC<CreativityFormPageProps> = ({ creativityType, onNavigate }) => {
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const { title } = useTemplateInfo(creativityType);

  const { formConfig, formData, errors, fields, isFormComplete, handleFieldChange, setFieldError, validateForm } =
    useCreativityForm(creativityType);

  const { handleSubmitFormBanner, isCreating } = useSubmitFormCreativity({
    creativityType,
    onNavigate,
    validateForm
  });

  if (!formConfig) {
    return (
      <div style={{ maxWidth: '100%', margin: `0 ${spacing.lg}px` }}>
        <Text>Creatividad no encontrada: {creativityType}</Text>
      </div>
    );
  }

  return (
    <PageContainer style={{ marginBottom: spacing.lg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
        <PageTitle>Nueva creatividad</PageTitle>
        <Title style={{ color: '#68717F', fontSize: fontSize.xl }}>- {title}</Title>
      </div>
      <Text type='secondary' style={{ fontSize: fontSize.xl, marginBottom: spacing.lg, display: 'flex' }}>
        Personalizá tu diseño
      </Text>

      <Row
        gutter={screens.lg ? [24, 24] : [0, 40]}
        style={{
          border: '1px solid #f0f0f0',
          backgroundColor: 'white',
          padding: screens.xs ? spacing.sm : spacing.md
        }}
      >
        <Col
          xs={24}
          lg={12}
          style={{
            paddingRight: screens.md ? spacing.lg : 0,
            borderRight: screens.md ? '1px solid #f0f0f0' : 'none'
          }}
        >
          <div
            style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing.lg }}
          >
            <div style={{ width: '100%', padding: spacing.sm, borderRadius: 6, backgroundColor: '#F9FAFB' }}>
              <div style={{ display: 'flex', marginBottom: spacing.xs }}>
                <Text style={{ fontSize: fontSize.lg, color: '#0e1a26' }}>Template seleccionado</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
                <Text
                  type='secondary'
                  style={{
                    fontSize: fontSize.lg,
                    display: 'block',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}
                >
                  {title}
                </Text>
                <Button
                  type='link'
                  size={screens.xs ? 'small' : 'middle'}
                  style={{
                    color: tokens.colors.brands.blue[500],
                    padding: 0,
                    fontWeight: 500,
                    fontSize: fontSize.base
                  }}
                  onClick={() => onNavigate('create-asset')}
                >
                  Cambiar
                </Button>
              </div>
            </div>

            <CreativityForm
              creativityType={creativityType}
              formData={formData}
              errors={errors}
              fields={fields}
              isFormComplete={isFormComplete}
              onFieldChange={handleFieldChange}
              setFieldError={setFieldError}
              onSubmit={() => handleSubmitFormBanner(formData)}
              submitLabel='Crear'
              isSubmitting={isCreating}
            />
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <DemoPreview creativityType={creativityType} formData={formData} />
        </Col>
      </Row>
    </PageContainer>
  );
};
